import React, { useState, useEffect, useMemo } from 'react';
import {
  Table,
  Button,
  Card,
  Input,
  DatePicker,
  Space,
  Typography,
  message,
  Tag,
  Tooltip,
  Row,
  Col,
  Statistic,
  Radio,
  Select,
  Divider,
  Badge,
  Empty,
  Spin,
  Alert,
  Progress,
  Modal,
  Tabs,
  Descriptions,
  List,
  Avatar,
  Segmented
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  PrinterOutlined,
  ExportOutlined,
  HomeOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  FileTextOutlined,
  BarChartOutlined,
  UnorderedListOutlined,
  TableOutlined,
  PercentageOutlined,
  WarningOutlined,
  InfoCircleOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import axios from 'axios';

import PageContainer from '../components/PageContainer';
import { BASE_URL } from '../utils/constants';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const HabitacionesLibres = () => {

  const urlReportServer = JSON.parse(localStorage.getItem('urlReportServer') || '{}');    
  // Estados principales
  const [habitaciones, setHabitaciones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [filtroTipo, setFiltroTipo] = useState('TODAS');
  const [vista, setVista] = useState('tabla');
  const [estadisticas, setEstadisticas] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);

  const [rangoMinMax, setRangoMinMax] = useState({ minimo: null, maximo: null });  
  
  // Estados para filtros
  const [fechas, setFechas] = useState({
    desde: dayjs(),
    hasta: dayjs().add(1, 'days')
  });
  const [rangoHabs, setRangoHabs] = useState({
    desde: '',
    hasta: ''
  });
  const [tipoHabitacion, setTipoHabitacion] = useState('');
  
  // Estados para resumen
  const [resumenTipos, setResumenTipos] = useState([]);
  const [ocupacion, setOcupacion] = useState(0);
  
  // Configuración de columnas de la tabla
  const columns2 = [
    {
      title: 'Habitación',
      dataIndex: 'NumHab',
      key: 'NumHab',
      width: 100,
      fixed: 'left',
      sorter: (a, b) => a.NumHab - b.NumHab,
      render: (numHab) => (
        <Tag 
          color="blue" 
          style={{ 
            fontSize: '16px', 
            fontWeight: 'bold', 
            padding: '4px 12px',
            borderRadius: '20px'
          }}
        >
          <HomeOutlined style={{ marginRight: 6 }} />
          {numHab}
        </Tag>
      )
    },
    {
      title: 'Tipo',
      dataIndex: 'TipoHab',
      key: 'TipoHab',
      width: 120,
      render: (tipo) => {
        const tipos = {
          'IN': { label: 'Individual', color: 'cyan', icon: '👤' },
          'DO': { label: 'Doble', color: 'green', icon: '👥' },
          'TR': { label: 'Triple', color: 'orange', icon: '👥👤' },
          'CU': { label: 'Cuádruple', color: 'purple', icon: '👥👥' },
          'QU': { label: 'Quíntuple', color: 'red', icon: '👥👥👤' }
        };
        const config = tipos[tipo] || { label: tipo, color: 'default', icon: '🏠' };
        return (
          <Tag color={config.color} style={{ padding: '4px 10px', borderRadius: '16px' }}>
            {config.icon} {config.label}
          </Tag>
        );
      },
      filters: [
        { text: 'Individuales', value: 'IN' },
        { text: 'Dobles', value: 'DO' },
        { text: 'Triples', value: 'TR' },
        { text: 'Cuádruples', value: 'CU' },
        { text: 'Quíntuples', value: 'QU' }
      ],
      onFilter: (value, record) => record.TipoHab === value
    },
    {
      title: 'SubTipo',
      dataIndex: 'SubTipo',
      key: 'SubTipo',
      width: 120,
      render: (subtipo) => subtipo || '—'
    },
    {
      title: 'Desde',
      dataIndex: 'Desde',
      key: 'Desde',
      width: 120,
      render: (desde) => desde ? dayjs(desde).format('DD/MM/YYYY') : '—',
      sorter: (a, b) => dayjs(a.Desde).unix() - dayjs(b.Desde).unix()
    },
    {
      title: 'Hasta',
      dataIndex: 'Hasta',
      key: 'Hasta',
      width: 120,
      render: (hasta) => hasta ? dayjs(hasta).format('DD/MM/YYYY') : '—',
      sorter: (a, b) => dayjs(a.Hasta).unix() - dayjs(b.Hasta).unix()
    },
    {
      title: 'Días Libres',
      dataIndex: 'Dias',
      key: 'Dias',
      width: 110,
      align: 'center',
      sorter: (a, b) => a.Dias - b.Dias,
      render: (dias) => (
        <Badge 
          count={dias} 
          style={{ 
            backgroundColor: dias > 30 ? '#52c41a' : dias > 7 ? '#1890ff' : '#faad14',
            borderRadius: '12px',
            padding: '0 8px'
          }}
          showZero
        />
      )
    },
    {
      title: 'Estado',
      key: 'estado',
      width: 100,
      align: 'center',
      render: (_, record) => {
        const hoy = dayjs();
        const desde = dayjs(record.Desde);
        const hasta = dayjs(record.Hasta);
        
        if (desde.isAfter(hoy)) {
          return <Tag color="processing" icon={<InfoCircleOutlined />}>Próxima</Tag>;
        } else if (hasta.isBefore(hoy)) {
          return <Tag color="default">Pasada</Tag>;
        } else {
          return <Tag color="success" icon={<CheckCircleOutlined />}>Disponible</Tag>;
        }
      }
    },
    // {
    //   title: 'Acciones',
    //   key: 'actions',
    //   width: 100,
    //   fixed: 'right',
    //   render: (_, record) => (
    //     <Tooltip title="Ver detalles">
    //       <Button
    //         type="link"
    //         icon={<EyeOutlined />}
    //         onClick={() => handleViewDetails(record)}
    //       />
    //     </Tooltip>
    //   )
    // }
  ];

const columns = [
    {
      title: 'Habitación',
      dataIndex: 'NumHab',
      key: 'NumHab',
      width: 100,
      fixed: 'left',
      sorter: (a, b) => a.NumHab - b.NumHab,
      render: (numHab) => (
        <Tag 
          color="blue" 
          style={{ 
            fontSize: '16px', 
            fontWeight: 'bold', 
            padding: '4px 12px',
            borderRadius: '20px'
          }}
        >
          <HomeOutlined style={{ marginRight: 6 }} />
          {numHab}
        </Tag>
      )
    },
    {
      title: 'Tipo',
      dataIndex: 'TipoHab',
      key: 'TipoHab',
      width: 120,
      render: (tipo) => {
        const tipos = {
          'IN': { label: 'Individual', color: 'cyan', icon: '👤' },
          'DO': { label: 'Doble', color: 'green', icon: '👥' },
          'TR': { label: 'Triple', color: 'orange', icon: '👥👤' },
          'CU': { label: 'Cuádruple', color: 'purple', icon: '👥👥' },
          'QU': { label: 'Quíntuple', color: 'red', icon: '👥👥👤' }
        };
        const config = tipos[tipo] || { label: tipo, color: 'default', icon: '🏠' };
        return (
          <Tag color={config.color} style={{ padding: '4px 10px', borderRadius: '16px' }}>
            {config.icon} {config.label}
          </Tag>
        );
      },
      filters: [
        { text: 'Individuales', value: 'IN' },
        { text: 'Dobles', value: 'DO' },
        { text: 'Triples', value: 'TR' },
        { text: 'Cuádruples', value: 'CU' },
        { text: 'Quíntuples', value: 'QU' }
      ],
      onFilter: (value, record) => record.TipoHab === value
    },
    {
      title: 'SubTipo',
      dataIndex: 'SubTipo',
      key: 'SubTipo',
      width: 100,
      render: (subtipo) => subtipo || '—'
    },
    {
      title: 'Descripción',
      dataIndex: 'lkDescrip',
      key: 'lkDescrip',
      width: 200,
      ellipsis: true,
      render: (descrip) => descrip || '—'
    },
    {
      title: 'Desde',
      dataIndex: 'desde',
      key: 'desde',
      width: 110,
      render: (desde) => desde ? dayjs(desde).format('DD/MM/YYYY') : '—',
      sorter: (a, b) => dayjs(a.desde).unix() - dayjs(b.desde).unix()
    },
    {
      title: 'Hasta',
      dataIndex: 'Hasta',
      key: 'Hasta',
      width: 110,
      render: (hasta) => hasta ? dayjs(hasta).format('DD/MM/YYYY') : '—',
      sorter: (a, b) => dayjs(a.Hasta).unix() - dayjs(b.Hasta).unix()
    },
    {
      title: 'Días Libres',
      dataIndex: 'Dias',
      key: 'Dias',
      width: 100,
      align: 'center',
      sorter: (a, b) => a.Dias - b.Dias,
      render: (dias) => (
        <Badge 
          count={dias} 
          style={{ 
            backgroundColor: dias > 30 ? '#52c41a' : dias > 7 ? '#1890ff' : '#faad14',
            borderRadius: '12px',
            padding: '0 8px'
          }}
          showZero
        />
      )
    },
    {
      title: 'Días Libres (Total)',
      dataIndex: 'DiasLibres',
      key: 'DiasLibres',
      width: 120,
      align: 'center',
      render: (dias) => `${dias} días`
    },
    // {
    //   title: 'Acciones',
    //   key: 'actions',
    //   width: 80,
    //   fixed: 'right',
    //   render: (_, record) => (
    //     <Tooltip title="Ver detalles">
    //       <Button
    //         type="link"
    //         icon={<EyeOutlined />}
    //         onClick={() => handleViewDetails(record)}
    //       />
    //     </Tooltip>
    //   )
    // }
  ];  
  
  // Columnas para vista resumida
  const columnsResumidas = [
    {
      title: 'Días Libres',
      dataIndex: 'Dias',
      key: 'Dias',
      width: 100,
      render: (dias) => (
        <Tag color="blue" style={{ fontSize: '14px', fontWeight: 'bold' }}>
          {dias} {dias === 1 ? 'día' : 'días'}
        </Tag>
      )
    },
    {
      title: 'Habitaciones',
      dataIndex: 'Habitaciones',
      key: 'Habitaciones',
      render: (habitaciones) => (
        <Space wrap size="small">
          {habitaciones?.split(',').map((hab, idx) => (
            <Tag key={idx} color="cyan" style={{ fontSize: '13px' }}>
              {hab.trim()}
            </Tag>
          ))}
        </Space>
      )
    },
    {
      title: 'Tipo',
      dataIndex: 'TipoHab',
      key: 'TipoHab',
      width: 120,
      render: (tipo) => {
        const tipos = {
          'IN': 'Individual',
          'DO': 'Doble',
          'TR': 'Triple',
          'CU': 'Cuádruple',
          'QU': 'Quíntuple'
        };
        return tipos[tipo] || tipo;
      }
    },
    {
      title: 'Total',
      dataIndex: 'Total',
      key: 'Total',
      width: 80,
      align: 'center',
      render: (total) => (
        <Badge 
          count={total} 
          style={{ backgroundColor: '#1890ff' }}
          showZero
        />
      )
    }
  ];
  
  // Cargar datos iniciales
  useEffect(() => {
    cargarEstadisticas();
    cargarRangoHabitaciones();
  }, []);
  
  const cargarEstadisticas = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/habitaciones/estadisticas`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setEstadisticas(response.data.data);
      }
    } catch (error) {
      console.error('Error cargando estadísticas:', error);
    }
  };

  const cargarRangoHabitaciones = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/habitaciones/rango`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setRangoMinMax(response.data.data);
      }
    } catch (error) {
      console.error('Error cargando rango:', error);
    }
  };  
  
  const buscarHabitaciones = async () => {
    if (!fechas.desde || !fechas.hasta) {
      message.warning('Seleccione las fechas de búsqueda');
      return;
    }
    
    setSearching(true);
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const params = {
        fechaDesde: fechas.desde.format('YYYY-MM-DD'),
        fechaHasta: fechas.hasta.format('YYYY-MM-DD'),
        tipoHab: filtroTipo !== 'TODAS' ? filtroTipo : '',
        habDesde: rangoHabs.desde,
        habHasta: rangoHabs.hasta,
        tipoResumen: vista === 'resumido' ? 'resumido' : 'detallado'
      };
      
      const response = await axios.get(`${BASE_URL}/api/habitaciones/libres`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      
      if (response.data.success) {
        const data = response.data.data;
        
        if (vista === 'resumido') {
          // Procesar datos para vista resumida
          const resumen = {};
          data.forEach(item => {
            const key = `${item.Dias}_${item.TipoHab || 'TODAS'}`;
            if (!resumen[key]) {
              resumen[key] = {
                Dias: item.Dias,
                TipoHab: item.TipoHab || 'TODAS',
                Habitaciones: [],
                Total: 0
              };
            }
            resumen[key].Habitaciones.push(item.NumHab);
            resumen[key].Total++;
          });
          
          const resumenData = Object.values(resumen).map(item => ({
            ...item,
            Habitaciones: item.Habitaciones.sort((a, b) => a - b).join(', ')
          })).sort((a, b) => a.Dias - b.Dias);
          
          setHabitaciones(resumenData);
        } else {
          setHabitaciones(data);
        }
        
        // Calcular resumen por tipo de habitación
        const tiposResumen = {};
        data.forEach(item => {
          const tipo = item.TipoHab || 'OTROS';
          if (!tiposResumen[tipo]) {
            tiposResumen[tipo] = { total: 0, disponible: 0 };
          }
          tiposResumen[tipo].total++;
          if (item.Dias > 0) tiposResumen[tipo].disponible++;
        });
        
        setResumenTipos(Object.entries(tiposResumen).map(([tipo, stats]) => ({
          tipo,
          ...stats,
          porcentaje: stats.total > 0 ? Math.round((stats.disponible / stats.total) * 100) : 0
        })));
        
        // Calcular ocupación aproximada
        const totalHabs = data.length;
        const disponibles = data.filter(item => item.Dias > 0).length;
        setOcupacion(totalHabs > 0 ? Math.round(((totalHabs - disponibles) / totalHabs) * 100) : 0);
        
        message.success(`Se encontraron ${data.length} habitaciones`);
      }
    } catch (error) {
      console.error('Error buscando habitaciones:', error);
      message.error(error.response?.data?.error || 'Error al buscar habitaciones');
      setHabitaciones([]);
    } finally {
      setLoading(false);
      setSearching(false);
    }
  };
  
  const handleViewDetails = (record) => {
    setSelectedRow(record);
    setModalVisible(true);
  };
  
  const handleExport = async () => {
    try {
      message.loading('Generando exportación...', 1);
      
      const token = localStorage.getItem('token');
      const params = {
        fechaDesde: fechas.desde.format('YYYY-MM-DD'),
        fechaHasta: fechas.hasta.format('YYYY-MM-DD'),
        tipoHab: filtroTipo !== 'TODAS' ? filtroTipo : '',
        habDesde: rangoHabs.desde,
        habHasta: rangoHabs.hasta
      };
      
      const response = await axios.get(`${BASE_URL}/api/habitaciones/libres/export`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `habitaciones_libres_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      message.success('Exportación completada');
    } catch (error) {
      console.error('Error exportando:', error);
      message.error('Error al exportar los datos');
    }
  };
  
  const handleImprimir = async () => {
    const hideLoading = message.loading(`Generando informe....`, 0);  

    try {
      // Obtener token
      const token = localStorage.getItem('token');
    
      // Validar token
      if (!token) {
        throw new Error('No se encontró token de autenticación. Por favor, inicie sesión nuevamente.');
      }      

      // Construir consulta SQL segura (evitar inyección SQL)
      const sqlQuery = ` select * from [dbo].[DevuelveHabitacionesLibres] (999,'30/06/2026','01/07/2026') `;
      
      // Realizar la petición
      const response = await fetch(`${urlReportServer}/report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          // 'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify({
            hotelCode: 999,
            reportName: "Listado habitaciones libres",
            sqlQuery: sqlQuery
        }),
        mode: 'cors', // Asegurar que usa CORS
        credentials: 'omit' // No enviar cookies si no las necesitas
      });

      // Cerrar loading
      hideLoading();

      console.log(" response => ",response);
    

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
      
        let errorMsg = `Error ${response.status}`;
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.error) {
            errorMsg = errorData.error;
          }
        } catch (e) {
          // Si no es JSON, usar el texto
          if (errorText) {
            errorMsg = errorText;
          }
        }
        throw new Error(errorMsg);
      }

      // Obtener el texto de la respuesta antes de parsear
      const responseText = await response.text();
      console.log('Respuesta recibida (primeros 200 chars):', responseText.substring(0, 200));
    
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Error parseando JSON:', parseError);
        console.error('Texto recibido:', responseText);
      
        // Intentar limpiar el texto
        const cleanText = responseText
          .replace(/[\x00-\x1F\x7F-\x9F]/g, '') // Eliminar caracteres de control
          .replace(/\r/g, '')
          .replace(/\n/g, '');
      
        try {
          data = JSON.parse(cleanText);
        } catch (e) {
          throw new Error('El servidor devolvió datos inválidos. Contacte con el administrador.');
        }
      }
    
      if (!data.success) {
        throw new Error(data.error || 'Error al generar el reporte');
      }

      if (!data.pdfbase64) {
        throw new Error('No se recibió el PDF');
      }

      // Mostrar PDF
      try {
        // Limpiar el Base64 (eliminar posibles caracteres extraños)
        let pdfBase64 = data.pdfbase64
          .replace(/[\x00-\x1F\x7F-\x9F]/g, '') // Eliminar caracteres de control
          .replace(/\s/g, ''); // Eliminar espacios en blanco
      
        const byteCharacters = atob(pdfBase64); 
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
      
        // Abrir en nueva ventana
        window.open(url, '_blank');
      
        // Limpiar URL después de 10 segundos
        setTimeout(() => {
          URL.revokeObjectURL(url);
        }, 10000);
      } catch (pdfError) {
        console.error('Error mostrando PDF:', pdfError);
        throw new Error('Error al mostrar el PDF: ' + pdfError.message);
      }

    } catch (error) {
      // Cerrar loading si está abierto
      hideLoading();

      console.error('Error al generar informe:', error);

      // Manejar errores específicos
      let errorMessage = 'Ocurrió un error al generar el informe.';

      if (error.code === 'ECONNABORTED') {
        errorMessage = 'Tiempo de espera agotado. El servidor no responde.';
      } else if (error.response) {
        // El servidor respondió con un error
        const status = error.response.status;
        const data = error.response.data;

        switch (status) {
          case 400:
            errorMessage = 'Solicitud inválida. Verifique los datos enviados.';
            break;
          case 401:
            errorMessage = 'No autorizado. Su sesión ha expirado. Por favor, inicie sesión nuevamente.';
            break;
          case 403:
            errorMessage = 'No tiene permisos para generar este reporte.';
            break;
          case 404:
            errorMessage = 'El endpoint del reporte no fue encontrado.';
            break;
          case 500:
            errorMessage = 'Error interno del servidor. Por favor, intente más tarde.';
            break;
          default:
            errorMessage = data?.error || data?.message || `Error ${status}: ${error.response.statusText}`;
        }
      } else if (error.request) {
        // La petición se hizo pero no hubo respuesta
        errorMessage = 'El servidor no responde. Verifique su conexión a la red.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      // Mostrar error
      Modal.error({
        title: 'Error',
        content: errorMessage,
        onOk: () => {
          // Si es error de autenticación, redirigir al login
          if (errorMessage.includes('No autorizado') || errorMessage.includes('sesión ha expirado')) {
            // Redirigir al login
            // window.location.href = '/login';
          }
        }
      });
    }
  };
  
  const handleReset = () => {
    setFechas({
      desde: dayjs(),
      hasta: dayjs().add(1, 'days')
    });
    setRangoHabs({ desde: '', hasta: '' });
    setFiltroTipo('TODAS');
    setTipoHabitacion('');
    setHabitaciones([]);
    setResumenTipos([]);
    setOcupacion(0);
  };
  
  // Componente de filtros de búsqueda
  const FiltrosBusqueda = () => (
    <Card 
      style={{ marginBottom: 24, borderRadius: 12 }}
      bodyStyle={{ padding: '20px' }}
    >
      <Row gutter={[24, 16]}>
        <Col xs={24} md={12} lg={8}>
          <div style={{ marginBottom: 8 }}>
            <Text strong>
              <CalendarOutlined style={{ marginRight: 8 }} />
              Período de Consulta
            </Text>
          </div>
          <RangePicker
            value={[fechas.desde, fechas.hasta]}
            onChange={(dates) => {
              if (dates && dates[0] && dates[1]) {
                setFechas({ desde: dates[0], hasta: dates[1] });
              }
            }}
            style={{ width: '100%' }}
            format="DD/MM/YYYY"
            placeholder={['Fecha desde', 'Fecha hasta']}
          />
        </Col>
        
        <Col xs={24} md={12} lg={6}>
          <div style={{ marginBottom: 8 }}>
            <Text strong>Tipo de Habitación</Text>
          </div>
          <Select
            value={filtroTipo}
            onChange={setFiltroTipo}
            style={{ width: '100%' }}
            placeholder="Seleccionar tipo"
          >
            <Option value="TODAS">Todas</Option>
            <Option value="IN">Individuales</Option>
            <Option value="DO">Dobles</Option>
            <Option value="TR">Triples</Option>
            <Option value="CU">Cuádruples</Option>
            <Option value="QU">Quíntuples</Option>
          </Select>
        </Col>
        
        <Col xs={24} md={12} lg={5}>
          <div style={{ marginBottom: 8 }}>
            <Text strong>Habitación Desde</Text>
          </div>
          <Input
            type="number"
            placeholder="N° habitación"
            value={rangoHabs.desde}
            onChange={(e) => setRangoHabs({ ...rangoHabs, desde: e.target.value })}
            prefix={<HomeOutlined />}
          />
        </Col>
        
        <Col xs={24} md={12} lg={5}>
          <div style={{ marginBottom: 8 }}>
            <Text strong>Habitación Hasta</Text>
          </div>
          <Input
            type="number"
            placeholder="N° habitación"
            value={rangoHabs.hasta}
            onChange={(e) => setRangoHabs({ ...rangoHabs, hasta: e.target.value })}
            prefix={<HomeOutlined />}
          />
        </Col>
      </Row>
      
      <Divider style={{ margin: '16px 0' }} />
      
      <Row justify="end" gutter={16}>
        <Col>
          <Button 
            type="primary" 
            onClick={buscarHabitaciones} 
            loading={searching}
            icon={<SearchOutlined />}
            // size="large"
          >
            Buscar Habitaciones
          </Button>
        </Col>        
        {/* <Col>
          <Button onClick={handleReset} icon={<ReloadOutlined />}>
            Restablecer
          </Button>
        </Col> */}
        <Col>
          <Button onClick={handleImprimir} icon={<ReloadOutlined />}>
            Imprimir
          </Button>
        </Col>        

      </Row>
    </Card>
  );
  
  
  
  // Vista de tabla
  const VistaTabla = () => (
    <Table
      columns={columns}
      dataSource={habitaciones.filter(h => h.NumHab)}
      rowKey={(record) => record.NumHab || record.Dias + '_' + record.TipoHab}
      loading={loading}
      pagination={false}
      // scroll={{ x: 1000, y: 500 }}
      // pagination={{
      //   pageSize: 15,
      //   showSizeChanger: true,
      //   showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} habitaciones`,
      //   pageSizeOptions: ['10', '15', '20', '50']
      // }}
      size="middle"
      rowClassName={(record) => 
        record.Dias === 0 ? 'ant-table-row-disabled' : ''
      }
    />
  );
    
  return (
    <>
      <PageContainer
        title="Habitaciones Libres"
        subtitle="Consulta de disponibilidad de habitaciones por período"
        icon={<HomeOutlined />}
        loading={loading}
        onRefresh={buscarHabitaciones}
        // badgeCount={habitaciones.length}
        // badgeStatus="processing"
        extra={
          <Space>
            {/* <Segmented
              value={vista}
              onChange={setVista}
              options={[
                { value: 'tabla', icon: <TableOutlined />, label: 'Detallado' },
                { value: 'resumido', icon: <UnorderedListOutlined />, label: 'Resumido' }
              ]}
            /> */}
            <Tooltip title="Exportar a Excel">
              <Button
                icon={<ExportOutlined />}
                onClick={handleExport}
                disabled={habitaciones.length === 0}
              />
            </Tooltip>
            <Tooltip title="Imprimir Reporte">
              <Button
                icon={<PrinterOutlined />}
                onClick={handleImprimir}
                disabled={habitaciones.length === 0}
              />
            </Tooltip>
          </Space>
        }
      >
        {/* Filtros de búsqueda */}
        <FiltrosBusqueda />
        
        {/* Estadísticas */}
        {habitaciones.length > 0 && (
          <>
            {/* <EstadisticasPanel /> */}
            {/* <ResumenTiposPanel /> */}
          </>
        )}
        
        {/* Resultados */}
        {habitaciones.length > 0 ? (
          vista === 'tabla' ? <VistaTabla /> : <VistaResumida />
        ) : (
          !searching && (
            <Empty
              description={
                <span>
                  No hay datos para mostrar
                  <br />
                  <Text type="secondary">Seleccione fechas y presione "Buscar Habitaciones"</Text>
                </span>
              }
              style={{ marginTop: 50 }}
            />
          )
        )}
        
        {/* Loading overlay */}
        {searching && (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" tip="Buscando habitaciones disponibles..." />
          </div>
        )}
      </PageContainer>
      
      {/* Modal de detalles */}
      <Modal
        title={
          <span>
            <HomeOutlined style={{ marginRight: 8 }} />
            Detalles de Habitación {selectedRow?.NumHab}
          </span>
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setModalVisible(false)}>
            Cerrar
          </Button>,
          <Button key="print" type="primary" icon={<PrinterOutlined />} onClick={handleImprimir}>
            Imprimir
          </Button>
        ]}
        width={600}
      >
        {selectedRow && (
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="N° Habitación" span={2}>
              <Tag color="blue" style={{ fontSize: '16px', fontWeight: 'bold' }}>
                {selectedRow.NumHab}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Tipo">
              {selectedRow.TipoHab}
            </Descriptions.Item>
            <Descriptions.Item label="SubTipo">
              {selectedRow.SubTipo || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Fecha Desde">
              {selectedRow.Desde ? dayjs(selectedRow.Desde).format('DD/MM/YYYY') : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Fecha Hasta">
              {selectedRow.Hasta ? dayjs(selectedRow.Hasta).format('DD/MM/YYYY') : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Días Libres">
              <Badge 
                count={selectedRow.Dias} 
                style={{ 
                  backgroundColor: selectedRow.Dias > 30 ? '#52c41a' : '#1890ff',
                  borderRadius: '12px',
                  padding: '0 8px'
                }}
              />
            </Descriptions.Item>
            <Descriptions.Item label="Estado">
              {selectedRow.Dias > 0 ? (
                <Tag color="success" icon={<CheckCircleOutlined />}>Disponible</Tag>
              ) : (
                <Tag color="error" icon={<CloseCircleOutlined />}>Ocupada</Tag>
              )}
            </Descriptions.Item>
            {selectedRow.Txt && (
              <Descriptions.Item label="Observaciones" span={2}>
                <Alert message={selectedRow.Txt} type="info" showIcon />
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>
    </>
  );
};

export default HabitacionesLibres;